"use client";
import { useEffect, useRef, Suspense } from "react";
import Search from "../../elements/forms/search/Search";
import BottomHeader from "./more/bottomHeader/BottomHeader";
import MainHeader from "./more/mainHeader/MainHeader";
import TopHeader from "./more/topHeader/TopHeader";

/**
 * Header iki AYRI yapışkan parçaya bölünmüş durumda:
 *
 *   .header-main   → logo/arama/ikonlar — her zaman sticky (top: 0)
 *   .header-bottom → kategori şeridi    — masaüstünde sticky, mobilde DEĞİL
 *
 * Neden ayrı iki element: tek bir <header> içinde iç parçayı sticky yapmak
 * çalışmaz — sticky bir eleman yalnızca EBEVEYNİNİN kutusu içinde yapışır,
 * dolayısıyla menzili header'ın kendi yüksekliği kadar olurdu. İkisi de
 * PagesShell'deki `min-h-screen` kapsayıcının doğrudan çocuğu olduğu için
 * (Header bir fragment döndürüyor) yapışma menzili tüm sayfa oluyor.
 *
 * KAYDIRMA SIRASINDA HİÇBİR BOYUT DEĞİŞMİYOR — bu bilinçli. Önceki sürüm
 * mobilde BottomHeader'ı `display:none` yapıp header'ı kısaltıyordu; sticky
 * eleman akışta olduğu için altındaki sayfa kayıyor, tarayıcının scroll
 * anchoring'i scrollY'yi geri alıyor ve sentinel yeniden görünür olup sınıfı
 * kaldırıyordu → kapalı döngü → donma. Artık gizleme yok: mobilde kategori
 * şeridi sadece yapışmıyor, sayfayla birlikte yukarı kayıp gidiyor.
 */
const Header = () => {
  const mainRef = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Masaüstünde .header-bottom, .header-main'in hemen ALTINA yapışmalı; bunun
  // için üst parçanın yüksekliği gerekiyor. Sabit sayı yazmak kırılgan (logo
  // boyutu breakpoint'e göre değişiyor), o yüzden ölçülüp CSS değişkenine
  // yazılıyor. ResizeObserver yalnızca boyut gerçekten değişince çalışır —
  // kaydırma sırasında HİÇ iş yapmaz.
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;

    const apply = () => {
      document.documentElement.style.setProperty(
        "--header-main-h",
        `${el.offsetHeight}px`,
      );
    };
    apply();

    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Gölge için "sayfa kaydırıldı mı" bilgisi. Sınıf <body>'ye yazılıyor çünkü
  // gölgeyi taşıyan eleman breakpoint'e göre değişiyor: mobilde yapışan tek
  // parça .header-main, masaüstünde bloğun görsel alt kenarı .header-bottom.
  // Artık yalnızca box-shadow değiştiği için (layout değil) bu geçiş sadece
  // repaint; döngü kuramaz.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        document.body.classList.toggle("page-scrolled", !entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      document.body.classList.remove("page-scrolled");
    };
  }, []);

  return (
    <>
      <TopHeader />
      {/* Sticky sentinel — header'ın yapıştığı anı işaretler. Header'ın hemen
          üstünde durduğu için viewport'tan çıktığı an top:0'a yapıştığı andır;
          masaüstünde de (TopHeader 40px) mobilde de (TopHeader gizli, 0px)
          doğru çalışır. height:1 + marginBottom:-1 → net layout etkisi sıfır. */}
      <div ref={sentinelRef} aria-hidden style={{ height: 1, marginBottom: -1 }} />

      <header ref={mainRef} className="header-main">
        <div className="container-header">
          <MainHeader />
          {/* pb-2: mobilde kategori şeridi yapışmayıp kayıp gidince arama
              input'u header'ın alt kenarına yapışık kalıyordu. MainHeader'ın
              my-2'siyle aynı ölçü, üstle altta simetrik boşluk. */}
          <div className="block md:hidden pb-2">
            <Suspense fallback={null}>
              <Search />
            </Suspense>
          </div>
        </div>
      </header>

      <div className="header-bottom">
        <div className="container-minify">
          <BottomHeader />
        </div>
      </div>
    </>
  );
};

export default Header;
